import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAuthentication, useChannels } from '@connect-x-app/hybrid-state';
import { Dot, Text } from '@connect-x-app/hybrid-ui';
import { Channel, ChannelType } from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { categorizeChannels } from '@connect-x-app/shared-utils';
import { CHANNEL_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Icon } from '@connect-x-app/web-ui';
import classNames from 'classnames';

import './ChannelSidebar.scss';

export const ChannelSidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { channelId: currentChannelId, roomId } = useParams<{ channelId: string; roomId: string }>();

  const { roomChannels } = useChannels();

  const { isUserPartOfChannel } = useAuthentication();

  const { myChannels, otherChannels } = useMemo(() => {
    if (!roomChannels?.customChannels) {
      return { myChannels: [], otherChannels: [] };
    }
    return categorizeChannels(roomChannels.customChannels, isUserPartOfChannel);
  }, [roomChannels?.customChannels, isUserPartOfChannel]);

  const renderChannel = (channel: Channel, isOtherChannel = false) => {
    const isActive = channel.id === currentChannelId;
    const count = isOtherChannel ? (channel.totalMessagesCount ?? 0) : channel.unreadMessagesCount;
    const hasIcons = channel.type === ChannelType.PRIVATE || count > 0;

    return (
      <button
        className={classNames('channel-item', { 'has-icons': hasIcons, 'is-active': isActive })}
        key={channel.id}
        onClick={() => {
          navigate(`/dashboard/flightrooms/${roomId}/channel/${channel.id}`);
        }}
        type="button"
      >
        <Text as="span" className="channel-item__title" color={COLORS.zulu.$08} size="MD" wrap>
          {channel.title}
        </Text>
        {hasIcons ? (
          <span className="channel-item__icons">
            {channel.type === ChannelType.PRIVATE ? (
              <Icon name="privateChannel" testId={CHANNEL_TEST_IDS.channels.privateIcon} />
            ) : null}
            {count > 0 && (isOtherChannel || !isActive) ? (
              <Dot
                className={isOtherChannel ? 'dot--other-channel' : undefined}
                size="l"
                testId={CHANNEL_TEST_IDS.channels.unreadDot}
                value={count}
              />
            ) : null}
          </span>
        ) : null}
      </button>
    );
  };

  return (
    <div className="channels-sidebar" data-test={CHANNEL_TEST_IDS.channels.list}>
      {myChannels.length > 0 ? (
        <div className="channels-section">
          <Text className="channels-section__title" color={COLORS.zulu.$08} size="MD" uppercase weight="bold">
            {t('CHAT.CHANNELS.SECTIONS.MY_CHANNELS')}
          </Text>
          {myChannels.map(channel => renderChannel(channel))}
        </div>
      ) : null}
      {otherChannels.length > 0 ? (
        <div className="channels-section">
          <Text className="channels-section__title" color={COLORS.zulu.$08} size="MD" uppercase weight="bold">
            {t('CHAT.CHANNELS.SECTIONS.OTHERS')}
          </Text>
          {otherChannels.map(channel => renderChannel(channel, true))}
        </div>
      ) : null}
    </div>
  );
};
